import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Newspaper,
  Calendar,
  MousePointerClick,
  MoveHorizontal,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { rtdb } from "../config/firebase";

const colorMapping = {
  greenTeal: { gradient: "from-green-600 to-teal-600" },
  tealGreen: { gradient: "from-teal-600 to-green-600" },
  limeGreen: { gradient: "from-lime-600 to-green-600" },
};

export default function NewsSection() {
  const [hoveredNews, setHoveredNews] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const newsRef = ref(rtdb, "berita");
    onValue(newsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formatted = Object.entries(data).map(([key, item], index) => ({
          id: key,
          title: item.judul,
          excerpt:
            item.keterangan?.slice(0, 120) +
            (item.keterangan?.length > 120 ? "..." : ""),
          date: item.tanggal,
          image: item.fotoUrl,
          colorKey: ["greenTeal", "tealGreen", "limeGreen"][index % 3],
        }));
        // Mengambil 5 data terbaru
        setNewsData(formatted.reverse().slice(0, 5));
      }
    });
  }, []);

  const handleScroll = (event) => {
    const scrollLeft = event.target.scrollLeft;
    const cardWidth = event.target.clientWidth;
    const newIndex = Math.round(scrollLeft / cardWidth);

    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  };

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <section className="relative bg-white py-32 px-6 md:px-16 overflow-hidden">

      {/* === KODE BACKGROUND DIKEMBALIKAN === */}
      {/* 🌿 Background Decorative Animated */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.06, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 7, repeat: Infinity }}
          className="absolute top-1/3 right-1/4 w-96 h-96 bg-green-200/50 rounded-full blur-[100px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-teal-200/50 rounded-full blur-[100px]"
        ></motion.div>
        {/* Ini dia div untuk garis-garisnya */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.06)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.06)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* 📰 Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-10 text-center mb-20 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-300 rounded-full backdrop-blur-sm mb-6"
        >
          <Newspaper className="w-4 h-4 text-green-600" />
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Latest News
          </span>
        </motion.div>

        <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight">
          <span className="text-gray-900">Berita & </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-teal-600 to-green-700">
            Update
          </span>
        </h2>

        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="h-1 w-32 bg-gradient-to-r from-green-600 to-teal-600 rounded-full mx-auto mb-8"
        ></motion.div>

        <p className="text-lg text-gray-700 leading-relaxed font-light">
          Dapatkan informasi terbaru seputar kegiatan, inovasi, dan pencapaian
          Soney Raya.
        </p>
      </motion.div>

      {/* 🗞️ News Grid */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-3 gap-6 md:gap-10 pb-4 no-scrollbar"
        >
          {newsData.map((news, index) => {
            const isHovered = hoveredNews === index;
            const gradientClass = colorMapping[news.colorKey].gradient;

            return (
              <motion.div
                key={news.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                custom={index}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                onMouseEnter={() => setHoveredNews(index)}
                onMouseLeave={() => setHoveredNews(null)}
                onClick={() => navigate(`/news/${news.id}`)}
                className="group relative cursor-pointer snap-start w-full md:w-auto flex-shrink-0"
              >
                {/* 🌈 Glow Effect */}
                <motion.div
                  animate={{ opacity: [0, 0.25, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className={`absolute -inset-1 bg-gradient-to-r ${gradientClass} rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition duration-500`}
                ></motion.div>

                {/* 📰 Card */}
                <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-md transition-all duration-500 group-hover:scale-[1.03] group-hover:shadow-2xl h-full">
                  {/* Image */}
                  <motion.div
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.5 }}
                    className="relative h-56 overflow-hidden"
                  >
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                  </motion.div>

                  {/* Content */}
                  <div className="p-6 flex flex-col space-y-3">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{news.date}</span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 leading-snug group-hover:text-green-600 transition-colors duration-300">
                      {news.title}
                    </h3>

                    <p className="text-gray-600 text-sm leading-relaxed">
                      {news.excerpt}
                    </p>

                    {/* Hover Text */}
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{
                        opacity: isHovered ? 1 : 0,
                        y: isHovered ? 0 : 5,
                      }}
                      transition={{ duration: 0.4 }}
                      className="flex items-center justify-center gap-2 text-sm font-medium text-green-600 mt-4"
                    >
                      <MousePointerClick className="w-4 h-4" />
                      Klik untuk membuka
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Indikator Swipe & Dots (Hanya Tampil di Mobile) */}
        {newsData.length > 1 && (
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 md:hidden">
            {/* Petunjuk Geser */}
            <motion.div
              animate={{ x: [-3, 3, -3] }}
              transition={{
                repeat: Infinity,
                duration: 1.2,
                ease: "easeInOut",
              }}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <MoveHorizontal className="w-4 h-4" />
              <span className="font-mono">Geser</span>
            </motion.div>

            {/* Dots Pagination */}
            <div className="flex items-center justify-center gap-2">
              {newsData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    activeIndex === index
                      ? "w-6 bg-green-600"
                      : "w-2.5 bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        )}

        {/* 🌟 View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/news")}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-teal-500 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
          >
            <motion.div
              initial={{ x: "-100%" }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 bg-gradient-to-r from-teal-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            ></motion.div>
            <span className="relative">Lihat Semua Berita</span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
